import { addSeconds } from 'date-fns';
import { useEffect, useRef } from 'react';
import { useTimer } from 'react-timer-hook';

import type { UseQuizTimerProps } from '.';

export function useSectionTimers(quizTimerProps: UseQuizTimerProps) {
  const { quiz, attempt, isSuccess, currentSection, sectionTimers, setSectionTimers, onNavigateNext, onForceSubmit } = quizTimerProps;

  /*
    array of time left on every section
    kept as everything else in seconds
    null meaning no timer on section, 0 timer expired
    this also controls access to sections
  */

  const prevSelectedSection = useRef<number | null>(null);

  const masterTimer = useTimer({
    expiryTimestamp: new Date(),
    autoStart: false,
    onExpire: () => onExpireHandler(),
  });

  function onExpireHandler() {
    if (
      sectionTimers.every((t) => {
        return t == null || t == 0;
      })
    ) {
      onForceSubmit();
    } else {
      onNavigateNext();
    }
  }

  //wait for the data being loaded to init the array
  useEffect(() => {
    if (isSuccess && quiz && attempt) {
      //load timers from metadata
      setSectionTimers(attempt.body.metadata.timers);

      if (quiz.content[currentSection]?.time) {
        masterTimer.restart(addSeconds(new Date(), quiz.content[currentSection].time));
        prevSelectedSection.current = currentSection;
      }
    }
  }, [isSuccess]);

  //switch timers based on the currentSection
  useEffect(() => {
    //update prev section timer, if one present
    if (prevSelectedSection.current != null && sectionTimers[prevSelectedSection.current]) {
      const updatedTimers = [...sectionTimers];
      updatedTimers[prevSelectedSection.current] = masterTimer.totalSeconds;
      setSectionTimers(updatedTimers);
    }

    prevSelectedSection.current = currentSection;

    //set new timer if present, check for expiry is done by navigation hook
    if (sectionTimers[currentSection]) {
      masterTimer.restart(addSeconds(new Date(), sectionTimers[currentSection]));
    }
  }, [currentSection]);

  //return early if data not yet loaded
  if (!quiz || !attempt) {
    return {
      totalSeconds: null,
      sectionTimers: null,
    };
  }

  return {
    totalSeconds: masterTimer.totalSeconds,
    sectionTimers,
  };
}
